"use strict";
import { Token } from "@uniswap/sdk-core";
import { PrefetchBalancesWrapper } from "appGraphql/data/apollo/AdaptiveTokenBalancesProvider";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import OneInchSwap from "components/OneInchSwap/OneInchSwap";
import { SwitchLocaleLink } from "components/SwitchLocaleLink";
import TokenBalancesCard from "components/TokenBalancesCard";
import ChartSection, { useCreateTDPChartState } from "components/Tokens/TokenDetails/ChartSection";
import { PageWrapper } from "components/swap/styled";
import { useAccount } from "hooks/useAccount";
import { useDeferredComponent } from "hooks/useDeferredComponent";
import { PageType, useIsPage } from "hooks/useIsPage";
import { useModalState } from "hooks/useModalState";
import { useSelectedTokenState } from "hooks/useSelectedTokenAddress";
import { useResetOverrideOneClickSwapFlag } from "pages/Swap/settings/OneClickSwap";
import { useWebSwapSettings } from "pages/Swap/settings/useWebSwapSettings";
import { TDPProvider } from "pages/TokenDetails/TDPContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { MultichainContextProvider } from "state/multichain/MultichainContext";
import { useSwapCallback } from "state/sagas/transactions/swapSaga";
import { useWrapCallback } from "state/sagas/transactions/wrapSaga";
import { SwapAndLimitContextProvider } from "state/swap/SwapContext";
import { useInitialCurrencyState } from "state/swap/hooks";
import { Flex, Text, Tooltip, styled } from "ui/src";
import { zIndexes } from "ui/src/theme";
import { useUniswapContext } from "uniswap/src/contexts/UniswapContext";
import { useTokenWebQuery } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { getChainInfo } from "uniswap/src/features/chains/chainInfo";
import { useIsModeMismatch } from "uniswap/src/features/chains/hooks/useEnabledChains";
import { UniverseChainId } from "uniswap/src/features/chains/types";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import { useGetPasskeyAuthStatus } from "uniswap/src/features/passkey/hooks/useGetPasskeyAuthStatus";
import Trace from "uniswap/src/features/telemetry/Trace";
import { InterfaceEventName, InterfacePageName, ModalName } from "uniswap/src/features/telemetry/constants";
import { sendAnalyticsEvent } from "uniswap/src/features/telemetry/send";
import { TransactionSettingsContextProvider } from "uniswap/src/features/transactions/components/settings/contexts/TransactionSettingsContext";
import { TransactionSettingKey } from "uniswap/src/features/transactions/components/settings/slice";
import { SwapFormContextProvider } from "uniswap/src/features/transactions/swap/contexts/SwapFormContext";
import { selectFilteredChainIds } from "uniswap/src/features/transactions/swap/contexts/selectors";
import { useSwapPrefilledState } from "uniswap/src/features/transactions/swap/form/hooks/useSwapPrefilledState";
import { currencyToAsset } from "uniswap/src/features/transactions/swap/utils/asset";
import { CurrencyField } from "uniswap/src/types/currency";
import { SwapTab } from "uniswap/src/types/screens/interface";
import { isMobileWeb } from "utilities/src/platform";
import { isIFramed } from "utils/isIFramed";
import "./newSwapStyle.css";
export default function SwapPage() {
  const navigate = useNavigate();
  const location = useLocation();
  useFeatureFlag(FeatureFlags.AATestWeb);
  const accountDrawer = useAccountDrawer();
  const {
    initialInputCurrency,
    initialOutputCurrency,
    initialChainId,
    initialTypedValue,
    initialField,
    triggerConnect
  } = useInitialCurrencyState();
  useEffect(() => {
    if (triggerConnect) {
      accountDrawer.open();
      navigate(location.pathname, { replace: true });
    }
  }, [accountDrawer, triggerConnect, navigate, location.pathname]);
  return <Trace logImpression page={InterfacePageName.SwapPage}><PageWrapper><Swap
    chainId={initialChainId}
    initialInputCurrency={initialInputCurrency}
    initialOutputCurrency={initialOutputCurrency}
    initialTypedValue={initialTypedValue}
    initialIndependentField={initialField}
    syncTabToUrl={true}
    usePersistedFilteredChainIds
  /></PageWrapper>{location.pathname === "/swap" && <SwitchLocaleLink />}</Trace>;
}
export function Swap({
  initialInputCurrency,
  initialOutputCurrency,
  initialTypedValue,
  initialIndependentField,
  chainId,
  hideHeader = false,
  hideFooter = false,
  onCurrencyChange,
  syncTabToUrl,
  swapRedirectCallback,
  tokenColor,
  usePersistedFilteredChainIds = false
}) {
  const isExplorePage = useIsPage(PageType.EXPLORE);
  const isModeMismatch = useIsModeMismatch(chainId);
  const isSharedSwapDisabled = isModeMismatch && isExplorePage;
  const input = currencyToAsset(initialInputCurrency);
  const output = currencyToAsset(initialOutputCurrency);
  const { isSwapTokenSelectorOpen, swapOutputChainId } = useUniswapContext();
  const persistedFilteredChainIds = useSelector(selectFilteredChainIds);
  const prefilledState = useSwapPrefilledState({
    input,
    output,
    exactAmountToken: initialTypedValue ?? "",
    exactCurrencyField: initialIndependentField ?? CurrencyField.INPUT,
    selectingCurrencyField: isSwapTokenSelectorOpen ? CurrencyField.OUTPUT : void 0,
    selectingCurrencyChainId: swapOutputChainId,
    skipFocusOnCurrencyField: isMobileWeb,
    filteredChainIdsOverride: usePersistedFilteredChainIds ? persistedFilteredChainIds : void 0
  });
  return <MultichainContextProvider initialChainId={chainId}><TransactionSettingsContextProvider settingKey={TransactionSettingKey.Swap}><SwapAndLimitContextProvider
    initialInputCurrency={initialInputCurrency}
    initialOutputCurrency={initialOutputCurrency}
  ><PrefetchBalancesWrapper><SwapFormContextProvider prefilledState={prefilledState} hideSettings={hideHeader} hideFooter={hideFooter}><div style={{ width: "100%" }}>{isSharedSwapDisabled && <DisabledSwapOverlay />}<UniversalSwapFlow
    hideHeader={hideHeader}
    hideFooter={hideFooter}
    syncTabToUrl={syncTabToUrl}
    swapRedirectCallback={swapRedirectCallback}
    onCurrencyChange={onCurrencyChange}
    prefilledState={prefilledState}
    tokenColor={tokenColor}
  /></div></SwapFormContextProvider></PrefetchBalancesWrapper></SwapAndLimitContextProvider></TransactionSettingsContextProvider></MultichainContextProvider>;
}
const SWAP_TABS = [SwapTab.Swap, SwapTab.Buy];
const TAB_TYPE_TO_LABEL = {
  [SwapTab.Swap]: (t) => t("swap.form.header"),
  [SwapTab.Limit]: (t) => t("swap.limit"),
  [SwapTab.Send]: (t) => t("send.title"),
  [SwapTab.Buy]: (t) => t("common.buy.label"),
  [SwapTab.Sell]: (t) => t("common.sell.label")
};
const PATHNAME_TO_TAB = {
  "/swap": SwapTab.Swap,
  "/send": SwapTab.Send,
  "/limit": SwapTab.Limit,
  "/buy": SwapTab.Buy,
  "/sell": SwapTab.Sell
};
function UniversalSwapFlow({
  hideHeader = false,
  hideFooter = false,
  disableTokenInputs = false,
  syncTabToUrl = true,
  prefilledState,
  onCurrencyChange,
  swapRedirectCallback,
  tokenColor
}) {
  const [currentTab, setCurrentTab] = useState(SwapTab.Swap);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const swapCallback = useSwapCallback();
  const wrapCallback = useWrapCallback();
  const LimitFormWrapper = useDeferredComponent(
    () => import("pages/Swap/Limit/LimitForm").then((module) => ({
      default: module.LimitFormWrapper
    }))
  );
  const BuyForm = useDeferredComponent(
    () => import("pages/Swap/Buy/BuyForm").then((module) => ({
      default: module.BuyForm
    }))
  );
  const { openModal: openSendFormModal } = useModalState(ModalName.Send);
  useEffect(() => {
    if (pathname === "/send") {
      setCurrentTab(SwapTab.Swap);
      if (!isIFramed()) {
        openSendFormModal();
      }
    } else {
      setCurrentTab(PATHNAME_TO_TAB[pathname] ?? SwapTab.Swap);
    }
  }, [pathname, setCurrentTab, openSendFormModal]);
  const onTabClick = useCallback(
    (tab) => {
      sendAnalyticsEvent(InterfaceEventName.SwapTabClicked, { tab });
      if (syncTabToUrl) {
        navigate(`/${tab}`, { replace: true });
      } else {
        setCurrentTab(tab);
      }
    },
    [navigate, syncTabToUrl, setCurrentTab]
  );
  const isFiatOffRampEnabled = useFeatureFlag(FeatureFlags.FiatOffRamp);
  const SWAP_TAB_OPTIONS = useMemo(() => {
    return SWAP_TABS.filter((tab) => {
      if (tab === SwapTab.Sell && !isFiatOffRampEnabled) {
        return false;
      }
      return true;
    }).map((tab) => ({
      value: tab,
      display: <Text
        variant="buttonLabel3"
        hoverStyle={{ color: "$neutral1" }}
        color={currentTab === tab ? "$neutral1" : "$neutral2"}
        tag="h1"
      >{TAB_TYPE_TO_LABEL[tab](t)}</Text>
    }));
  }, [t, currentTab, isFiatOffRampEnabled]);
  const swapSettings = useWebSwapSettings();
  const resetDisableOneClickSwap = useResetOverrideOneClickSwapFlag();
  const connectorId = useAccount().connector?.id;
  const passkeyAuthStatus = useGetPasskeyAuthStatus(connectorId);
  const TOKENS = [
    {
      symbol: "WETH",
      // conventional placeholder for native ETH (use 0x0 if your lib expects that)
      address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
      decimals: 18,
      name: "Wrapped Ether",
      logo: "/images/logos/weth.png"
    },
    {
      symbol: "WBTC",
      address: "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599",
      decimals: 8,
      name: "Wrapped Bitcoin",
      logo: "/images/logos/wbtc.png"
    },
    {
      symbol: "MATIC",
      address: "0x7D1AfA7B718fb893dB30A3aBc0Cfc608AaCfeBB0",
      decimals: 18,
      name: "Matic Token",
      logo: "/images/logos/matic.png"
    },
    {
      symbol: "rETH",
      address: "0xae78736cd615f374d3085123a210448e74fc6393",
      decimals: 18,
      name: "Rocket Pool ETH",
      logo: "/images/logos/reth.png"
    }
  ];
  const [selected, setSelected] = useState(TOKENS[1]);
  let tokenState = useSelectedTokenState();
  let wbtcAddress = "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599";
  const chainInfo = getChainInfo(UniverseChainId.Mainnet);
  const tokenQuery = useTokenWebQuery({
    variables: { address: selected.address, chain: chainInfo.backendChain.chain },
    errorPolicy: "all"
  });
  const chartState = useCreateTDPChartState(selected.address, chainInfo.backendChain.chain);
  const mockCurrency = new Token(
    1,
    // chainId
    selected.address,
    // address
    selected.decimals,
    // decimals
    selected.symbol,
    // symbol
    selected.name
    // name
  );
  const contextValue = useMemo(() => {
    return {
      currency: mockCurrency,
      // not needed for chart to work
      currencyChain: chainInfo.backendChain.chain,
      currencyChainId: chainInfo.id,
      address: wbtcAddress,
      currencyWasFetchedOnChain: false,
      tokenQuery,
      chartState,
      multiChainMap: {},
      tokenColor: "#f7931a"
      // use your own color if needed
    };
  }, [tokenQuery, chartState, selected]);
  const styles = {
    wrapper: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      width: "200px",
      fontFamily: "Inter, sans-serif",
      marginBottom: "20px"
    },
    select: {
      appearance: "none",
      padding: "10px 14px",
      borderRadius: "8px",
      background: "#141414",
      color: "#fff",
      border: "none",
      outline: "none",
      /* subtle inner glow */
      boxShadow: "inset 0 0 0 1px rgba(0,255,233,.3), inset 0 4px 18px rgba(0,255,233,.25)",
      cursor: "pointer"
    },
    addressLabel: {
      fontSize: "12px",
      color: "#7ee8ff",
      wordBreak: "break-all"
    }
  };
  const [fdv, setFdv] = useState(0);
  const [vol, setVol] = useState(0);
  async function setChartData(contractAddress) {
    const url = `https://api.coingecko.com/api/v3/coins/ethereum/contract/${contractAddress}`;
    try {
      setFdv(0);
      setVol(0);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      console.log("CHARTDATA::", data);
      const fdv2 = data?.market_data?.fully_diluted_valuation?.usd;
      const volume24h = data?.market_data?.total_volume?.usd;
      if (fdv2 === void 0 || volume24h === void 0) {
        throw new Error("FDV or 24h volume not found in response.");
      }
      setFdv(fdv2);
      setVol(volume24h);
    } catch (err) {
      console.error("Failed to fetch token stats:", err.message);
    }
  }
  const { address, isConnected } = useAccount();
  useEffect(() => {
    setChartData(selected.address);
  }, [selected]);
  return <TDPProvider contextValue={contextValue}><div style={styles.wrapper}>{
    /* dropdown */
  }<select
    style={styles.select}
    value={selected.symbol}
    onChange={(e) => {
      const token = TOKENS.find((t2) => t2.symbol === e.target.value);
      setSelected(token);
    }}
  >{TOKENS.map((t2) => <option key={t2.symbol} value={t2.symbol} style={{ display: "flex", gap: "10px" }}><img style={{ width: "100px" }} src={t2.logo} alt="logo" /> <p>{t2.symbol}</p></option>)}</select></div><div className="newSwap"><div style={{ width: "70%" }} className="swap-main"><ChartSection symbol={selected} fdv={fdv} vol={vol} /></div><div className="swap-main">{address ? <OneInchSwap address={address} isConnected={isConnected} /> : <OneInchSwap address={`0x${""}`} isConnected={false} />}</div></div><div style={{ marginTop: "20px" }}><TokenBalancesCard /></div></TDPProvider>;
}
const DisabledOverlay = styled(Flex, {
  position: "absolute",
  width: "100%",
  height: "100%",
  zIndex: zIndexes.overlay
});
const DisabledSwapOverlay = () => {
  const { t } = useTranslation();
  return <DisabledOverlay cursor="not-allowed"><Tooltip placement="left-start"><Tooltip.Content animationDirection="left"><Tooltip.Arrow /><Text variant="body4">{t("testnet.unsupported")}</Text></Tooltip.Content><Tooltip.Trigger position="relative" width="100%" height="100%"><DisabledOverlay /></Tooltip.Trigger></Tooltip></DisabledOverlay>;
};
